/**
 * Created by rathompson on 11/1/21
 * File: FiltersBar
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import {
  Select,
  Button,
  noop,
  Bold
} from '@panwds/react-ui'
import { ResetIcon } from '@panwds/icons'
import { IDENTITY_TYPES } from '../../../constants/IAMConstants'
import { ARRAY } from '../../../../common/utils/Utils'
import { getVarValue } from '../../../../common/utils/Sparky'
import {
  FF_IAM_FILTER_ROLES,
  FF_IAM_FILTER_INHERITED
} from '../../../../common/utils/FeatureFlags'
import GlobalContext from '../../../../common/context/GlobalContext'
import {
  LABEL_INHERITED_ANY
} from '../../../constants/LabelConstants'

const canFilterRoles = getVarValue(FF_IAM_FILTER_ROLES, false)
const canFilterInherited = getVarValue(FF_IAM_FILTER_INHERITED, false)

/**
 *   Table filters bar
 */
const FiltersBar = ({ apps, roles: _roles, inherited, setFilters }) => {

  const { treeReducer = {} } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer

  const [type, setType] = useState(null)
  const [app, setApp] = useState(null)
  const [role, setRole] = useState(null)
  const [inheritedBy, setInheritedBy] = useState(null)

  useEffect(() => {
    setType(null)
    setApp(null)
    setRole(null)
    setInheritedBy(null)
  }, [apps])

  const identityItems = useMemo(() => [
    {
      id: 'all',
      value: <Bold>{'All Identities'}</Bold>,
    },
    {
      id: IDENTITY_TYPES.USER,
      value: 'All Users',
    },
    {
      id: IDENTITY_TYPES.SERVICE_ACCOUNT,
      value: 'All Service Accounts',
    },
    {
      id: 'none',
      value: 'Identities Without Roles',
    },
  ], [])

  const appsItems = useMemo(() => {
    const items = ARRAY.hasArray(apps) ? [...apps] : []
    items.unshift({
      id: null,
      value: <Bold>{items.length ? `All Apps (${items.length})` : 'All Apps'}</Bold>,
    })
    return items
  }, [apps])

  const roleItems = useMemo(() => {
    const items = ARRAY.hasArray(_roles) ? [..._roles] : []
    return [
      {
        id: 'all',
        value: <Bold>{ items.length ? `All Roles (${items.length})` : 'All Roles' }</Bold>,
      },
      ...items,
    ]
  }, [_roles])

  const inheritedItems = useMemo(() => {
    const items = ARRAY.hasArray(inherited) ? [...inherited] : []
    return [
      {
        id: 'all',
        value: <Bold>{LABEL_INHERITED_ANY}</Bold>,
      },
      ...items
    ]
  }, [inherited])

  useEffect(() => {
    setFilters({ type, app, role, inheritedBy })
  }, [type, app, role, inheritedBy, setFilters])

  const onResetClick = useCallback(() => {
    setType(null)
    setApp(null)
    setRole(null)
    setInheritedBy(null)
    setFilters({ type: null, app: null, inheritedBy: null, role: null })
  }, [setFilters])

  return (
    <div id="filters-container" className="tw-flex tw-flex-row tw-justify-between tw-pt-2 tw-pb-2 tw-pr-3 tw-pl-3 tw-border-gray-200 dark:tw-border-gray-950 tw-border-t tw-border-b tw-border-l-0 tw-border-r-0 tw-border-solid">
      <div className="tw-flex tw-flex-row tw-gap-2">
        <Select
          addClassName='cmm-modal-select'
          fitToItemWidth
          selectedItem={type ?? identityItems[0]}
          items={identityItems}
          onChange={change => {
            setType(change.selectedItem)
          }}
        />
        <Select
          addClassName='cmm-modal-select'
          fitToItemWidth
          selectedItem={app ?? appsItems[0]}
          items={appsItems}
          onChange={change => {
            setApp(change.selectedItem)
          }}
        />
        {canFilterRoles && (
          <Select
            addClassName='cmm-modal-select'
            fitToItemWidth
            selectedItem={role ?? roleItems[0]}
            items={roleItems}
            onChange={change => {
              setRole(change.selectedItem)
            }}
          />
        )}
        {canFilterInherited && !selectedTreeNode.isAbsoluteRoot && (
          <Select
            selectedItem={inheritedBy ?? inheritedItems[0]}
            items={inheritedItems}
            onChange={change => {
              setInheritedBy(change.selectedItem)
            }}
          />
        )}
      </div>
      <Button
        aria-label={'reset-all'}
        icon={<ResetIcon size='sm' />}
        appearance="tertiary"
        onClick={onResetClick}
      />
    </div>
  )
}

FiltersBar.propTypes = {}

FiltersBar.defaultProps = {
  instances: [],
  setFilters: noop,
}
export default FiltersBar
